/* For testing purposes */

import { Controller } from '@hotwired/stimulus';
import { distroDirectService } from '../scripts/distro-direct';
import { EDDReleaseTrackAuthority } from '../scripts/distro-direct-types';

const USER = {
  userFirstName: import.meta.env.VITE_DISTRO_DIRECT_USER_FIRST_NAME || '',
  userLastName: import.meta.env.VITE_DISTRO_DIRECT_USER_LAST_NAME || '',
  userCountry: import.meta.env.VITE_DISTRO_DIRECT_USER_COUNTRY || '',
  userEmail: import.meta.env.VITE_DISTRO_DIRECT_USER_EMAIL || '',
  userPassword: import.meta.env.VITE_DISTRO_DIRECT_USER_PASSWORD || '',
  userUniqueId: import.meta.env.VITE_DISTRO_DIRECT_USER_UNIQUE_ID || '',
};

const RELEASE_ID = 223518;
const RELEASE_UPC = 9360932205852;
const TRACK_1_ID = 2100683973;
const TRACK_1_ISRC = 'AUMEV2442972';

const DATA_STORAGE_NAME = 'DD-COMPONENTS-DATA';

/**
 * The key is the name of the field in the storage
 * The value is the name of the field in the controller
 */
const DATA_STORAGE_FIELDS = Object.freeze({
  token: 'tokenFieldTarget',
  releaseId: 'releaseIdFieldTarget',
  userData: 'user',
});

export default class extends Controller {
  #distroDirectService = distroDirectService;
  static targets = [
    'tokenField',
    'releaseIdField',
    'releaseArtworkInput',
    'uploadTrackInput',
    'analyticsIframe',
    'userForm',
  ];

  initialize() {
    this.loadData();
    const user = this.user || USER;
    this.setUserData(user);

    this.userFormTarget.addEventListener('change', this.handleUserFormChange);
  }

  // User Data
  // ---------------------------------------------------------------------------
  setUserData(userData) {
    this.user = JSON.parse(JSON.stringify(userData));

    Array.from(this.userFormTarget.elements).forEach((element) => {
      if (element.name in this.user) {
        element.value = this.user[element.name];
      }
    });
  }

  handleUserFormChange = (event) => {
    if (event.target.name in this.user) {
      this.user[event.target.name] = event.target.value;
    }
    this.saveData({ userData: this.user });
  };

  resetUserData() {
    this.setUserData(USER);
    this.saveData({ userData: USER });
  }

  clearUserData() {
    const data = Object.fromEntries(Object.keys(USER).map((key) => [key, '']));
    this.setUserData(data);
    this.saveData({ userData: data });
  }

  togglePasswordVisibility() {
    this.userFormTarget.querySelector('input[name="userPassword"]').type =
      this.userFormTarget.querySelector('input[name="userPassword"]').type === 'password'
        ? 'text'
        : 'password';
  }

  // Handle data storage
  // ---------------------------------------------------------------------------
  loadData() {
    const data = window.sessionStorage.getItem(DATA_STORAGE_NAME);
    if (data) {
      const parsedData = JSON.parse(data);

      Object.entries(parsedData).forEach(([key, value]) => {
        if (key in DATA_STORAGE_FIELDS) {
          const field = DATA_STORAGE_FIELDS[key];

          if (this[field] && this[field] instanceof HTMLElement) {
            this[field].value = value;
          } else if (field) {
            this[field] = value;
          } else {
            console.warn('Data field/property not found');
          }
        }
      });

      this.#distroDirectService.userToken = parsedData.token;
    }
  }

  saveData(passedData) {
    const data = Object.fromEntries(
      Object.entries(DATA_STORAGE_FIELDS).map(([key, value]) => {
        if (this[value] && this[value] instanceof HTMLElement) {
          return [key, this[value].value];
        } else if (this[value]) {
          return [key, this[value]];
        } else {
          console.warn('Data field/property not found');
          return [key, 'Data field/property not found'];
        }
      }),
    );

    const dataToSave = !passedData
      ? data
      : {
          ...data,
          ...passedData,
        };

    window.sessionStorage.setItem(DATA_STORAGE_NAME, JSON.stringify(dataToSave));
    this.loadData();
  }

  clearData() {
    const dataFields = Object.keys(DATA_STORAGE_FIELDS);
    this.saveData(Object.fromEntries(dataFields.map((field) => [field, ''])));
  }

  clearToken() {
    this[DATA_STORAGE_FIELDS.token].value = '';
    this.saveData();
  }

  saveDataClick() {
    this.saveData();
  }

  //----------------------------------------------------------------------------
  // DD API calls
  //----------------------------------------------------------------------------
  async activate() {
    const res = await this.#distroDirectService.activate(this.user);
    console.log('activate:: res:', res);
    this.tokenFieldTarget.value = res.access_token;
    this.saveData();
  }

  async lastNameUpdate() {
    const data = {
      new_lastname: this.user.userLastName,
      firstname: this.user.userFirstName,
      country: this.user.userCountry,
      email: this.user.userEmail,
      password: this.user.userPassword,
      user_unique_id: this.user.userUniqueId,
    };

    const res = await this.#distroDirectService.lastNameUpdate(data);
    console.log('lastNameUpdate:: res:', res);
  }

  async firstNameUpdate() {
    const data = {
      new_firstname: this.user.userFirstName,
      lastname: this.user.userLastName,
      country: this.user.userCountry,
      email: this.user.userEmail,
      password: this.user.userPassword,
      user_unique_id: this.user.userUniqueId,
    };

    const res = await this.#distroDirectService.firstNameUpdate(data);
    console.log('firstNameUpdate:: res:', res);
  }

  async countryUpdate() {
    const data = {
      new_country: this.user.userCountry,
      firstname: this.user.userFirstName,
      lastname: this.user.userLastName,
      email: this.user.userEmail,
      password: this.user.userPassword,
      user_unique_id: this.user.userUniqueId,
    };

    const res = await this.#distroDirectService.countryUpdate(data);
    console.log(`countryUpdate:: res:`, res);
  }

  async emailUpdate() {
    const data = {
      new_email: this.user.userEmail,
      firstname: this.user.userFirstName,
      lastname: this.user.userLastName,
      country: this.user.userCountry,
      password: this.user.userPassword,
      user_unique_id: this.user.userUniqueId,
    };

    const res = await this.#distroDirectService.emailUpdate(data);
    console.log(`emailUpdate:: res:`, res);
  }

  async passwordUpdate() {
    const data = {
      new_password: this.user.userPassword,
      firstname: this.user.userFirstName,
      lastname: this.user.userLastName,
      country: this.user.userCountry,
      email: this.user.userEmail,
      user_unique_id: this.user.userUniqueId,
    };

    const res = await this.#distroDirectService.passwordUpdate(data);
    console.log(`emailUpdate:: res:`, res);
  }

  async getUserDetails() {
    const res = await this.#distroDirectService.getUserDetails(this.user.userUniqueId);
    console.log(`getUserDetails:: res:`, res);
  }

  async getMainArtists() {
    const res = await this.#distroDirectService.getMainArtists(this.user.userUniqueId);
    console.log(`getMainArtists:: res:`, res);
  }

  async getFeatureArtists() {
    const res = await this.#distroDirectService.getFeatureArtists(this.user.userUniqueId);
    console.log(`getFeatureArtists:: res:`, res);
  }

  async getLanguages() {
    const res = await this.#distroDirectService.getLanguages();
    console.log(`getLanguages:: res:`, res);
  }

  async getGenres() {
    const res = await this.#distroDirectService.getGenres();
    console.log(`getGenres:: res:`, res);
  }

  async getRecordLabels() {
    const res = await this.#distroDirectService.getRecordLabels(this.user.userUniqueId);
    console.log(`getRecordLabels:: res:`, res);
  }

  async getStores() {
    const res = await this.#distroDirectService.getStores();
    console.log(`getStores:: res:`, res);
  }

  async getCountries() {
    const res = await this.#distroDirectService.getCountries();
    console.log(`getCountries:: res:`, res);
  }

  promptForReleaseIdIfNecessary() {
    if (!this.releaseIdFieldTarget.value) {
      this.releaseIdFieldTarget.value = prompt('Enter release id', RELEASE_ID);
      this.saveData();
    }

    return this.releaseIdFieldTarget.value;
  }

  async getReleaseByID() {
    const release_id = this.promptForReleaseIdIfNecessary();
    const res = await this.#distroDirectService.getReleaseByID(this.user.userUniqueId, release_id);
    console.log(`getReleaseByID:: res:`, res);
  }

  async getCatalogueByType() {
    const res = await this.#distroDirectService.getCatalogueByType(this.user.userUniqueId, 'all');
    console.log(`getCatalogueByType:: res:`, res);
  }

  async getTracksByReleaseWithReleaseID() {
    const release_id = this.promptForReleaseIdIfNecessary();
    const res = await this.#distroDirectService.getTracksByRelease(
      this.user.userUniqueId,
      release_id,
    );
    console.log(`getTracksByReleaseWithReleaseID:: res:`, res);
  }

  async getTracksByReleaseWithReleaseUPC() {
    const releaseUPC = prompt('Provide the UPC of the Release:', RELEASE_UPC);
    const res = await this.#distroDirectService.getTracksByRelease(
      this.user.userUniqueId,
      '',
      releaseUPC,
    );
    console.log(`getTracksByReleaseWithReleaseUPC:: res:`, res);
  }

  async getTracksWithTrackID() {
    const trackId = prompt('Set track ID:', TRACK_1_ID);
    const res = await this.#distroDirectService.getTracks(this.user.userUniqueId, trackId);
    console.log(`getTracksWithTrackID:: res:`, res);
  }

  async getTracksWithTrackISRC() {
    const trackISRC = prompt('Provide the ISRC of the track:', TRACK_1_ISRC);
    const res = await this.#distroDirectService.getTracks(this.user.userUniqueId, '', trackISRC);
    console.log(`getTracksWithTrackISRC:: res:`, res);
  }

  async getComposerTypes() {
    const res = await this.#distroDirectService.getComposerTypes();
    console.log(`getComposerTypes:: res:`, res);
  }

  async getSpotifyUsers() {
    const artistName = prompt('Enter artist name, eg.: Tester');
    const res = await this.#distroDirectService.getSpotifyUsers(this.user.userUniqueId, artistName);
    console.log(`getSpotifyUsers:: res:`, res);
  }

  async getAppleUsers() {
    const artistName = prompt('Enter artist name, eg.: Tester');
    const res = await this.#distroDirectService.getAppleUsers(this.user.userUniqueId, artistName);
    console.log(`getAppleUsers:: res:`, res);
  }

  async getComposersByType() {
    const types = await this.#distroDirectService.getComposerTypes();
    const type = prompt(
      'Enter composer type, available types: ' +
        types.data.map((t) => t.join(': ')).join(', ') +
        '\n Provide the id of chosen type.',
    );
    const res = await this.#distroDirectService.getComposersByType(this.user.userUniqueId, type);
    console.log(`getComposersByType:: res:`, res);
  }

  async addMainArtist() {
    const name = prompt('Enter artist name');
    const res = await this.#distroDirectService.addMainArtist(this.user.userUniqueId, name);
    console.log(`addMainArtist:: res:`, res);
  }

  async addFeatureArtist() {
    const name = prompt('Enter artist name');
    const res = await this.#distroDirectService.addFeatureArtist(this.user.userUniqueId, name);
    console.log(`addFeatureArtist:: res:`, res);
  }

  async addComposer() {
    const name = prompt('Enter composer name');
    const types = await this.#distroDirectService.getComposerTypes();
    const type = prompt(
      'Enter composer type, available types: ' +
        types.data.map((t) => t.join(': ')).join(', ') +
        '\n Provide the id of choosen type.',
    );

    const res = await this.#distroDirectService.addComposer(this.user.userUniqueId, name, type);
    console.log(`addComposer:: res:`, res);
  }

  async addRecordLabel() {
    const name = prompt('Enter record label name');
    const res = await this.#distroDirectService.addRecordLabel(this.user.userUniqueId, name);
    console.log(`addRecordLabel:: res:`, res);
  }

  async setAppleID() {
    const artistName = prompt('Enter artist name');
    const id = prompt('Enter Apple ID');
    const res = await this.#distroDirectService.setAppleID(
      this.user.userUniqueId,
      artistName,
      Number(id),
    );
    console.log(`setAppleID:: res:`, res);
  }

  async setSpotifyURI() {
    const artistName = prompt('Enter artist name');
    const uri = prompt('Enter Spotify URI');
    const res = await this.#distroDirectService.setSpotifyURI(
      this.user.userUniqueId,
      artistName,
      uri,
    );
    console.log(`setSpotifyURI:: res:`, res);
  }

  async startRelease() {
    const title = prompt('Enter release title');
    let mainArtists = prompt('Enter main artists, separated by comma or Variuos artists');

    if (mainArtists !== 'Various Artists') {
      mainArtists = mainArtists.split(',');
    }

    const res = await this.#distroDirectService.startRelease(
      this.user.userUniqueId,
      title,
      mainArtists,
    );
    console.log(`startRelease:: res:`, res);

    this.releaseIdFieldTarget.value = res.data;
    this.saveData();
  }

  async setReleaseDate() {
    const release_id = this.promptForReleaseIdIfNecessary();
    const release_date = prompt('Enter release date in format: D/M/YYYY');

    const data = {
      release_type: 'new',
      release_date,
      original_release_date: '',
      preorder_date: '',
    };

    const res = await this.#distroDirectService
      .getReleaseService(this.user.userUniqueId, release_id)
      .setReleaseDate(data);

    console.log(`setReleaseDate:: res:`, res);
  }

  async setReleaseDateForNewRelease() {
    const release_id = this.promptForReleaseIdIfNecessary();
    const release_date = prompt('Enter release date in format: D/M/YYYY');

    const data = {
      release_date,
      preorder_date: '',
    };

    const res = await this.#distroDirectService
      .getReleaseService(this.user.userUniqueId, release_id)
      .setReleaseDateForNewRelease(data);

    console.log(`setReleaseDateForNewRelease:: res:`, res);
  }

  async setReleaseDateForOldRelease() {
    const release_id = this.promptForReleaseIdIfNecessary();
    const original_release_date = prompt('Enter release original release date in format: D/M/YYYY');

    const data = {
      original_release_date,
    };

    const res = await this.#distroDirectService
      .getReleaseService(this.user.userUniqueId, release_id)
      .setReleaseDateForOldRelease(data);

    console.log(`setReleaseDateForOldRelease:: res:`, res);
  }

  async setReleaseCopyright() {
    const release_id = this.promptForReleaseIdIfNecessary();
    const copyrightYear = prompt('Enter copyright year');
    const copyrightHolder = prompt('Enter copyright holder (leave empty to use main artist)');

    const res = await this.#distroDirectService
      .getReleaseService(this.user.userUniqueId, release_id)
      .setCopyright(Number(copyrightYear), copyrightHolder);

    console.log(`setCopyright:: res:`, res);
  }

  async setReleaseCountries() {
    const release_id = this.promptForReleaseIdIfNecessary();
    const countries = prompt('Enter countries, separated by comma, leave empty for all');

    const res = await this.#distroDirectService
      .getReleaseService(this.user.userUniqueId, release_id)
      .setCountries(countries ? countries.split(',') : 'ALL');

    console.log(`setCountries:: res:`, res);
  }

  async setReleaseGenre() {
    const release_id = this.promptForReleaseIdIfNecessary();
    const type = prompt('Enter genre type (primary or secondary)');
    const genre = prompt('Enter genre code (from getGenres)');

    const res = await this.#distroDirectService
      .getReleaseService(this.user.userUniqueId, release_id)
      .setGenre(type, genre);

    console.log(`setGenre:: res:`, res);
  }

  async setReleaseLanguage() {
    const release_id = this.promptForReleaseIdIfNecessary();
    const language = prompt('Enter language code (from getLanguages)');

    const res = await this.#distroDirectService
      .getReleaseService(this.user.userUniqueId, release_id)
      .setLanguage(language);

    console.log(`setLanguage:: res:`, res);
  }

  async setReleaseStores() {
    const release_id = this.promptForReleaseIdIfNecessary();
    const stores = prompt('Enter stores, obtained from getStores, separated by comma');

    const res = await this.#distroDirectService
      .getReleaseService(this.user.userUniqueId, release_id)
      .setStores(stores.split(','));

    console.log(`setStores:: res:`, res);
  }

  async setReleaseRecordLabel() {
    const release_id = this.promptForReleaseIdIfNecessary();
    const recordLabel = prompt('Enter record label code (from getRecordLabels)');

    const res = await this.#distroDirectService
      .getReleaseService(this.user.userUniqueId, release_id)
      .setRecordLabel(recordLabel);

    console.log(`setRecordLabel:: res:`, res);
  }

  async setReleaseArtwork() {
    const file = this.releaseArtworkInputTarget.files[0];

    if (!file) {
      alert('No file selected');
      return;
    }

    const release_id = this.promptForReleaseIdIfNecessary();
    const res = await this.#distroDirectService
      .getReleaseService(this.user.userUniqueId, release_id)
      .setArtwork(file);

    console.log(`setReleaseArtwork:: res:`, res);
  }

  async uploadTrack() {
    const file = this.uploadTrackInputTarget.files[0];
    const mainArtists = prompt('Enter main artists, separated by comma or Various Artists');

    if (!file) {
      alert('No file selected');
      return;
    }

    const release_id = this.promptForReleaseIdIfNecessary();
    const trackName = prompt('Set name for the track', 'Some track 1');
    const res = await this.#distroDirectService
      .getReleaseService(this.user.userUniqueId, release_id)
      .uploadTrack({
        name: trackName,
        mainArtists: mainArtists.includes(',') ? mainArtists.split(',') : mainArtists,
        // featureArtists: ['Fe Artist 2'],
        authority: EDDReleaseTrackAuthority.own,
        composers: [
          // { code: '4', name: 'Mixer Name' },
          // { code: '1', name: 'Composer Name' },
          // { code: '9', name: 'Engineer Name' },
        ],
        // isrc: 'AUMEV2445214',
        // isExplicit: true,
        // producer: 'Hello Producer',
        // tikTokStartTime: 80,
        // language: '29',
        file,
      });

    console.log(`uploadTrack:: res:`, res);
  }

  async submitRelease() {
    const release_id = this.promptForReleaseIdIfNecessary();
    const res = await this.#distroDirectService
      .getReleaseService(this.user.userUniqueId, release_id)
      .submitRelease();

    console.log(`submitRelease:: res:`, res);
  }

  async discardCatalogue() {
    const release_id = this.promptForReleaseIdIfNecessary();
    const res = await this.#distroDirectService
      .getReleaseService(this.user.userUniqueId, release_id)
      .discardCatalogue();

    console.log(`discardCatalogue:: res:`, res);
  }

  async deleteTrack() {
    const release_id = this.promptForReleaseIdIfNecessary();
    const trackId = prompt('Provide track ID');
    const res = await this.#distroDirectService
      .getReleaseService(this.user.userUniqueId, release_id)
      .deleteTrack(trackId);

    console.log(`deleteTrack:: res:`, res);
  }

  async getAnalytics() {
    const res = await this.#distroDirectService.getAnalytics(this.user.userUniqueId);
    console.log(`getAnalytics:: res:`, res);
    console.log('this.hasAnalyticsIframeTarget', this.hasAnalyticsIframeTarget);
    if (this.hasAnalyticsIframeTarget && res.URL) {
      this.analyticsIframeTarget.src = res.URL;
      this.analyticsIframeTarget.removeAttribute('hidden');
    }
  }
}
